/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LoggerStore } from "../../logger/logger-store"
import type { Result } from "../../network/http-client"
import { makeDeleteRequest, makeGetRequest, makePostRequest, makePutRequest } from "../../network/http-client"

import type { OrganizationStore } from "./organization-store"
import type { Organization, OrganizationCreate, OrganizationDetail } from "./organization-types"

enum Endpoint {
    organization = "/api/private/organization"
}

export class OrganizationService {
    constructor(
        private readonly organizationStore: OrganizationStore,
        private readonly logger: LoggerStore
    ) {}

    async getOrganizations(): Promise<Result<Organization[]>> {
        const orgs = await makeGetRequest<Organization[]>(Endpoint.organization, {})
        if (orgs.success && orgs.data) {
            this.organizationStore.setOrganizations(orgs.data)
        }
        if (!orgs.success) {
            this.logger.logApiError("Get organizations failed.", orgs.apiError, orgs.error)
        }
        return orgs
    }

    async getOrganization(orgId: number): Promise<Result<OrganizationDetail>> {
        const org = await makeGetRequest<OrganizationDetail>(`${Endpoint.organization}/${orgId}`, {})
        if (org.success && org.data) {
            this.organizationStore.setSelectedOrg(org.data)
        }
        if (!org.success) {
            this.logger.logApiError("Get organization failed.", org.apiError, org.error)
        }
        return org
    }

    async createOrganization(
        name: string,
        artifactoryUsername: string,
        artifactoryPassword: string
    ): Promise<Result<Organization>> {
        const payload: OrganizationCreate = {
            name,
            artifactoryUsername,
            artifactoryPassword
        }
        const org = await makePostRequest<OrganizationCreate, Organization>(`${Endpoint.organization}`, {}, payload)
        if (org.success && org.data) {
            this.organizationStore.appendOrganization(org.data)
            this.logger.logOk("Organization created successfully.")
        }
        if (!org.success) {
            this.logger.logApiError("Create organization failed.", org.apiError, org.error)
        }
        return org
    }

    async updateOrganization(
        orgId: number,
        name: string,
        artifactoryUsername: string,
        artifactoryPassword: string
    ): Promise<Result<Organization>> {
        const payload: OrganizationCreate = {
            name,
            artifactoryUsername,
            artifactoryPassword
        }
        const org = await makePutRequest<OrganizationCreate, Organization>(
            `${Endpoint.organization}/${orgId}`,
            {},
            payload
        )
        if (org.success && org.data) {
            this.organizationStore.updateOrganization(org.data)
        }
        if (!org.success) {
            this.logger.logApiError("Update organization failed.", org.apiError, org.error)
        }
        return org
    }

    async deleteOrganization(orgId: number): Promise<Result<void>> {
        const res = await makeDeleteRequest<void>(`${Endpoint.organization}/${orgId}`, {})
        if (res.success) {
            this.organizationStore.removeOrganization(orgId)
            this.logger.logOk("Organization deleted successfully.")
        }
        if (!res.success) {
            this.logger.logApiError("Delete organization failed.", res.apiError, res.error)
        }
        return res
    }
}
